import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { map, Observable } from 'rxjs';
import { Event } from 'src/app/models/event';
import { User } from 'src/app/models/user';
import { DateFormatEnum } from 'src/app/pipes/dateUserLocale.pipe';
import { DataSourceFetchParams, DataSourceFetchResult } from 'src/app/services/data-source.service';
import { EventService } from 'src/app/services/event.service';
import { FiltersFactoryService } from 'src/app/services/filters-factory.service';
import { UserService } from 'src/app/services/user.service';
import {
  DefaultFilterKey,
  FilterData,
  FilterKeyValuePair,
} from 'src/app/standalones/components/generic-filters-view/filters.interface';
import { SelectionListModalSections } from 'src/app/standalones/components/modals/selection-modal/selection-list-modal/selection-list-modal.component';

export interface ReassignUserDialogData {
  event: Event;
}

@Component({
  selector: 'app-reassign-user-dialog',
  templateUrl: './reassign-user-dialog.component.html',
  styleUrls: ['./reassign-user-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReassignUserDialogComponent implements OnInit {
  @ViewChild('nameHeader', { static: true }) nameHeader!: TemplateRef<void>;
  @ViewChild('nameCell', { static: true }) nameCell!: TemplateRef<{ $implicit: Event }>;
  @ViewChild('timeHeader', { static: true }) timeHeader!: TemplateRef<void>;
  @ViewChild('timeCell', { static: true }) timeCell!: TemplateRef<{ $implicit: Event }>;

  readonly DateFormatEnum = DateFormatEnum;

  filters: Observable<FilterData[]>;
  columns!: SelectionListModalSections<Event>['columns'];

  constructor(
    @Inject(MAT_DIALOG_DATA) protected data: ReassignUserDialogData,
    private eventService: EventService,
    private filtersFactoryService: FiltersFactoryService,
    private userService: UserService,
  ) {
    this.fetch = this.fetch.bind(this);

    const initialValues: FilterKeyValuePair[] = [
      { key: DefaultFilterKey.EventsAtSameTimeFilter, value: true },
    ];
    const selectedEventSite = this.userService.getSiteFromInstitution(this.data.event.site);
    if (selectedEventSite) {
      initialValues.push({
        key: DefaultFilterKey.SitesFilter,
        value: [selectedEventSite],
      });
    }

    this.filters = this.filtersFactoryService.createReassignableEventsFilters(
      this.data.event,
      initialValues,
    );
  }

  ngOnInit(): void {
    this.columns = {
      name: { size: 0.5, header: this.nameHeader, cell: this.nameCell },
      time: { header: this.timeHeader, cell: this.timeCell },
    };
  }

  fetch(params: DataSourceFetchParams): Observable<DataSourceFetchResult<Event>> {
    return this.eventService.getReassignableEvents(this.data.event._id, params).pipe(
      map((res) => ({
        items: res.data.map((event: Event) => ({
          ...event,
          users: [
            ...(event.host_ids ?? []),
            ...(event.participants?.map((participant) => participant.user_id) ?? []),
            ...(event.auto_added_ids ?? []),
          ] as User[],
        })),
        totalItems: res.totalRecords,
      })),
    );
  }
}
